import {
  AccountBalanceWalletOutlined,
  AccountCircleOutlined,
  AddCircleOutline,
  AdminPanelSettingsOutlined,
  Apartment,
  ArticleOutlined,
  AssessmentOutlined,
  AutoAwesomeOutlined,
  BarChartOutlined,
  BoltOutlined,
  BusinessOutlined,
  CalendarMonthOutlined,
  DonutSmallOutlined,
  FolderOutlined,
  GroupsOutlined,
  HelpOutlineOutlined,
  HistoryOutlined,
  InsightsOutlined,
  LockOutlined,
  NotificationsOutlined,
  PictureAsPdfOutlined,
  PlaylistAddCheckOutlined,
  SettingsOutlined,
  ShowChartOutlined,
  StoreOutlined,
  SyncOutlined,
  TableChartOutlined,
  WaterfallChartOutlined,
  WebAssetOutlined,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { SuperSearchResult } from 'api';
import { FileTypeIcon } from 'components/ui';
import { getInitials } from 'utilities/getInitials';
import { SUPER_SEARCH_COMMANDS } from './SuperSearch.constants';
import { ResultFirmAvatar } from './SuperSearch.styles';
import type { SuperSearchDisplayItem } from './SuperSearch.types';

const PAGE_ICONS: Record<string, SvgIconComponent> = {
  intelligence: InsightsOutlined,
  valuations: AssessmentOutlined,
  waterfalls: WaterfallChartOutlined,
  documents: FolderOutlined,
  summary: ArticleOutlined,
  financials: BarChartOutlined,
  'cap-table': TableChartOutlined,
  'fund-ownership': DonutSmallOutlined,
  'breakpoint-analysis': ShowChartOutlined,
  'cash-flow-ledger': AccountBalanceWalletOutlined,
  waterfall: WaterfallChartOutlined,
  'document-generation-jobs': AutoAwesomeOutlined,
  'user-management': AdminPanelSettingsOutlined,
  'firm-profile': BusinessOutlined,
  'single-sign-on': LockOutlined,
  scim: SyncOutlined,
  'comp-groups': GroupsOutlined,
  'audit-logs': HistoryOutlined,
  'account-settings': AccountCircleOutlined,
  [SUPER_SEARCH_COMMANDS.CREATE_COMPANY]: StoreOutlined,
  [SUPER_SEARCH_COMMANDS.CREATE_MEASUREMENT_DATE]: CalendarMonthOutlined,
  [SUPER_SEARCH_COMMANDS.EXCEL_EXPORT]: TableChartOutlined,
  [SUPER_SEARCH_COMMANDS.BULK_ACTIONS]: PlaylistAddCheckOutlined,
  [SUPER_SEARCH_COMMANDS.PDF_EXPORT]: PictureAsPdfOutlined,
  [SUPER_SEARCH_COMMANDS.OPEN_NOTIFICATIONS]: NotificationsOutlined,
};

export const getSuperSearchResultIcon = (
  item: SuperSearchDisplayItem,
  sectionId: string,
  firmIconUrlsById: ReadonlyMap<number, string>
) => {
  if (item.result.type === SuperSearchResult.type.COMPANY) {
    return <StoreOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.FIRM) {
    const iconUrl = firmIconUrlsById.get(item.result.firm.id);

    if (iconUrl) {
      return (
        <ResultFirmAvatar alt="" src={iconUrl}>
          {getInitials({ name: item.result.firm.name })}
        </ResultFirmAvatar>
      );
    }

    return <Apartment />;
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return <FileTypeIcon fileType={item.result.document?.file_type} sx={{ color: 'inherit' }} />;
  }

  if (item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION) {
    return <TableChartOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION) {
    return <BarChartOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_VERSION) {
    return <AssessmentOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH) {
    return <AssessmentOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.WATERFALL_VIEW) {
    return <WaterfallChartOutlined />;
  }

  if (item.result.type === SuperSearchResult.type.CUSTOM_VIEW) {
    return item.result.custom_view?.page_type === 'valuations' ? <AssessmentOutlined /> : <InsightsOutlined />;
  }

  const PageIcon = item.page?.key ? PAGE_ICONS[item.page.key] : undefined;
  if (PageIcon) {
    return <PageIcon />;
  }

  if (sectionId === 'firm-actions') {
    return <BoltOutlined />;
  }

  if (sectionId === 'guided-tours') {
    return <HelpOutlineOutlined />;
  }

  if (sectionId === 'company-actions') {
    return <AddCircleOutline />;
  }

  if (sectionId === 'measurement-dates' || sectionId === 'switch-measurement-date') {
    return <CalendarMonthOutlined />;
  }

  if (sectionId === 'firm-settings') {
    return <SettingsOutlined />;
  }

  return <WebAssetOutlined />;
};
